import React, { useState } from "react";

/**
 * CareLogix™ ORACLE — Constitutional Logo Component (GIOS™)
 * Brand Standard v1.5.0 — Constitutional Pack
 *
 * Renders the official PNG asset (uploaded constitutional logo) as the primary
 * mark. If the PNG ever fails to load, falls back to a re-drawn inline SVG that
 * matches the constitutional emblem (Classical Financial Institution Temple,
 * Vision Eye, Growth Arrow, Global Sphere, Gold Shield) and a GIOS™ ribbon
 * (replacing the legacy "4.8" version stamp).
 *
 * Additive-only contract:
 *   - Default export preserved (no breaking imports anywhere in the app).
 *   - Accepts the same `size` prop as before.
 *   - New optional props: `variant` ("auto" | "png" | "svg"), `withRing` (bool),
 *     `className`, `title`.
 *
 * Animation hooks are CSS-driven (App.css), GPU-safe, and respect
 * prefers-reduced-motion.
 */

const PNG_SRC = "/brand/carelogix-oracle-gios-512.png";
const PNG_SRC_192 = "/brand/carelogix-oracle-gios-192.png";

function ConstitutionalSvg({ size = 44, title = "CareLogix ORACLE — GIOS" }) {
  // Re-drawn constitutional emblem (temple + vision eye + arrow + globe + shield + GIOS ribbon).
  // Designed to be crisp at any size and PDF-export safe.
  const s = size;
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 120 120"
      role="img"
      aria-label={title}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="cl-gios-gold" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#f6d97a" />
          <stop offset="45%" stopColor="#e8a020" />
          <stop offset="100%" stopColor="#a07a1f" />
        </linearGradient>
        <linearGradient id="cl-gios-emerald" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#0f3a2e" />
          <stop offset="100%" stopColor="#06231b" />
        </linearGradient>
        <radialGradient id="cl-gios-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#f5c842" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#f5c842" stopOpacity="0" />
        </radialGradient>
        <filter id="cl-gios-soft-glow">
          <feGaussianBlur stdDeviation="0.6" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Outer circular medallion */}
      <circle cx="60" cy="60" r="58" fill="url(#cl-gios-emerald)" stroke="url(#cl-gios-gold)" strokeWidth="1.4" />
      <circle cx="60" cy="60" r="55" fill="none" stroke="url(#cl-gios-gold)" strokeWidth="0.5" opacity="0.7" />

      {/* Soft ambient glow behind shield */}
      <circle cx="60" cy="56" r="34" fill="url(#cl-gios-glow)" />

      {/* Shield body */}
      <path
        d="M60 22 L88 31 V58 C88 73 76 84 60 90 C44 84 32 73 32 58 V31 Z"
        fill="url(#cl-gios-emerald)"
        stroke="url(#cl-gios-gold)"
        strokeWidth="2"
        filter="url(#cl-gios-soft-glow)"
      />

      {/* Classical Financial Institution Temple (replaces medical cross) */}
      {/* Pediment */}
      <polygon points="50,34 70,34 60,28" fill="url(#cl-gios-gold)" />
      <rect x="48.5" y="34" width="23" height="2.2" fill="url(#cl-gios-gold)" />
      {/* Three columns */}
      <rect x="51" y="37" width="2.2" height="9" fill="url(#cl-gios-gold)" />
      <rect x="58.9" y="37" width="2.2" height="9" fill="url(#cl-gios-gold)" />
      <rect x="66.8" y="37" width="2.2" height="9" fill="url(#cl-gios-gold)" />
      {/* Stylobate / base */}
      <rect x="49" y="46.5" width="22" height="2" fill="url(#cl-gios-gold)" />

      {/* Vision Eye (PERCEIVE™) */}
      <ellipse cx="60" cy="58" rx="11" ry="6.5" fill="none" stroke="url(#cl-gios-gold)" strokeWidth="1.6" />
      <circle cx="60" cy="58" r="3.4" fill="url(#cl-gios-gold)" />
      <circle cx="60" cy="58" r="1.4" fill="#06231b" />

      {/* Growth Arrow (ACT™) */}
      <line x1="68" y1="74" x2="80" y2="50" stroke="url(#cl-gios-gold)" strokeWidth="2" strokeLinecap="round" />
      <polygon points="80,50 75.5,52.5 77.6,56.2" fill="url(#cl-gios-gold)" />

      {/* GIOS ribbon (replaces 4.8) */}
      <path
        d="M44 92 L76 92 L80 96 L76 100 L44 100 L40 96 Z"
        fill="url(#cl-gios-emerald)"
        stroke="url(#cl-gios-gold)"
        strokeWidth="1.2"
      />
      <text
        x="60"
        y="98.4"
        textAnchor="middle"
        fontFamily="'Cinzel', 'Playfair Display', serif"
        fontWeight="700"
        fontSize="6.4"
        letterSpacing="1.4"
        fill="url(#cl-gios-gold)"
      >
        GIOS™
      </text>

      {/* Circular wordmark — top arc */}
      <defs>
        <path id="cl-gios-arc-top" d="M 14 60 A 46 46 0 0 1 106 60" />
      </defs>
      <text fontFamily="'Cinzel', serif" fontSize="6.6" letterSpacing="2.4" fill="url(#cl-gios-gold)">
        <textPath href="#cl-gios-arc-top" startOffset="50%" textAnchor="middle">
          CARELOGIX™ ORACLE
        </textPath>
      </text>
    </svg>
  );
}

export default function OracleLogo({
  size = 44,
  variant = "auto",
  withRing = false,
  className = "",
  title = "CareLogix ORACLE — Global Intelligence Operating System",
  alt = "CareLogix ORACLE — GIOS",
  style = {},
}) {
  const [pngFailed, setPngFailed] = useState(false);
  const useSvg = variant === "svg" || (variant === "auto" && pngFailed);

  const wrapperStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: size,
    height: size,
    borderRadius: withRing ? "50%" : 0,
    boxShadow: withRing ? "0 0 0 1px rgba(245, 200, 66, 0.35)" : "none",
    ...style,
  };

  if (useSvg) {
    return (
      <span className={`oracle-logo oracle-logo-svg ${className}`} style={wrapperStyle} title={title}>
        <ConstitutionalSvg size={size} title={title} />
      </span>
    );
  }

  // Choose closest pre-rendered PNG to reduce decode cost for small sizes.
  const src = size <= 96 ? PNG_SRC_192 : PNG_SRC;

  return (
    <span className={`oracle-logo oracle-logo-png ${className}`} style={wrapperStyle} title={title}>
      <img
        src={src}
        alt={alt}
        width={size}
        height={size}
        loading="eager"
        decoding="async"
        draggable={false}
        onError={() => setPngFailed(true)}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          display: "block",
          userSelect: "none",
        }}
        data-testid="oracle-logo-img"
      />
    </span>
  );
}

// Named export for places that want to force the SVG variant (PDF templates, etc.)
export { ConstitutionalSvg as OracleLogoSvg };
